import React from 'react';
import ReactTable from 'react-table';
import 'react-table/react-table.css';
import { Text } from '@cuemath/web-comps';

import {
  TableCard,
  Pill,
  ShareBarWrap,
  ShareBar,
  EmptyCell,
  CellStack,
  UnknownText,
  UI,
} from '../../styled-comps';
import {
  METRICS,
  METRIC_LABELS,
  EMPTY_MESSAGE,
  UNKNOWN_LABEL,
  UNKNOWN_REASONS,
} from '../../constants';
import { conversionPct, channelLabel, channelColor, sumRows } from '../../helpers';
import HoverHint from '../hover-hint';

// Label cell that explains '(unknown)' values with a hover tooltip.
const labelText = (value, unknownReason, variant = 'body3', color = UI.ink) =>
  value === UNKNOWN_LABEL && unknownReason ? (
    <HoverHint hint={unknownReason}>
      <UnknownText variant={variant} color={color}>
        {value}
      </UnknownText>
    </HoverHint>
  ) : (
    <Text variant={variant} color={color}>
      {value}
    </Text>
  );

// Rows may carry a `_cmp` object (same metric fields, comparison period) — when
// present, every metric/conversion cell shows the exact comparison value below
// the current one.

// Conversion pill with high/mid/low colour bands (mirrors the reference).
const PILL_BANDS = {
  high: { $bg: '#dcfce7', $fg: '#15803d' },
  mid: { $bg: '#fef9c3', $fg: '#854d0e' },
  low: { $bg: '#fee2e2', $fg: '#b91c1c' },
};

const ConvPill = ({ numerator, denominator }) => {
  const pct = conversionPct(numerator, denominator);
  if (pct === null) return <Text variant="body3" color={UI.muted}>—</Text>;
  const band = pct >= 50 ? 'high' : pct >= 25 ? 'mid' : 'low';
  return <Pill {...PILL_BANDS[band]}>{pct}%</Pill>;
};

const numberCell = (accessor, sortable = true) => ({
  Header: METRIC_LABELS[accessor],
  accessor,
  sortable,
  Cell: row => {
    const cmp = row.original._cmp;
    return (
      <CellStack>
        <Text variant={row.original.isTotal ? 'body2' : 'body3'} color={UI.ink}>
          {Number(row.value || 0).toLocaleString()}
        </Text>
        {cmp && (
          <Text variant="caption" color={UI.muted}>
            {`vs ${Number(cmp[accessor] || 0).toLocaleString()}`}
          </Text>
        )}
      </CellStack>
    );
  },
});

const convCell = (id, header, numerator, denominator, sortable = true) => ({
  Header: header,
  id,
  sortable,
  accessor: r => conversionPct(r[numerator], r[denominator]) || 0,
  Cell: row => {
    const cmp = row.original._cmp;
    const cmpPct = cmp ? conversionPct(cmp[numerator], cmp[denominator]) : null;
    return (
      <CellStack>
        <ConvPill numerator={row.original[numerator]} denominator={row.original[denominator]} />
        {cmp && (
          <Text variant="caption" color={UI.muted}>
            {`vs ${cmpPct === null ? '—' : `${cmpPct}%`}`}
          </Text>
        )}
      </CellStack>
    );
  },
});

// Non-sortable: these tables lead with a Total row — sorting would shuffle it
// into the middle of the breakdown rows.
const METRIC_AND_CONV_COLUMNS = () => [
  numberCell(METRICS.QL, false),
  numberCell(METRICS.TRIAL_SCHEDULED, false),
  numberCell(METRICS.TRIAL_DONE, false),
  convCell('ql_td', 'QL → TD', METRICS.TRIAL_DONE, METRICS.QL, false),
  numberCell(METRICS.ENROLLED, false),
  convCell('td_enr', 'TD → Enr', METRICS.ENROLLED, METRICS.TRIAL_DONE, false),
  convCell('ql_enr', 'QL → Enr', METRICS.ENROLLED, METRICS.QL, false),
];

// Columns for the Summary source-breakdown table (one row per DB channel).
export const buildSourceColumns = totalQl => [
  {
    Header: 'Source',
    accessor: 'channel',
    sortable: true,
    minWidth: 150,
    Cell: row => (
      <ShareBarWrap>
        <ShareBar $color={channelColor(row.value)} $width={10} />
        <Text variant="body3" color={UI.ink}>{channelLabel(row.value)}</Text>
      </ShareBarWrap>
    ),
  },
  numberCell(METRICS.QL),
  {
    Header: 'Share of QL',
    id: 'share',
    sortable: true,
    accessor: r => (totalQl ? Math.round((r[METRICS.QL] / totalQl) * 100) : 0),
    Cell: row => {
      const share = totalQl ? Math.round((row.original[METRICS.QL] / totalQl) * 100) : 0;
      return (
        <ShareBarWrap>
          <ShareBar $color={channelColor(row.original.channel)} $width={Math.max(4, share * 1.4)} />
          <Text variant="body3" color={UI.body}>{share}%</Text>
        </ShareBarWrap>
      );
    },
  },
  numberCell(METRICS.TRIAL_DONE),
  convCell('ql_td', 'QL → TD', METRICS.TRIAL_DONE, METRICS.QL),
  numberCell(METRICS.ENROLLED),
  convCell('td_enr', 'TD → Enr', METRICS.ENROLLED, METRICS.TRIAL_DONE),
  convCell('ql_enr', 'QL → Enr', METRICS.ENROLLED, METRICS.QL),
];

// Sortable page-breakdown table: every row is a contributing page (no total
// row, so sorting is safe). Rows sum exactly to the category totals.
export const buildContributorColumns = (header, unknownReason) => [
  {
    Header: header,
    accessor: 'label',
    sortable: true,
    minWidth: 240,
    Cell: row => labelText(row.value, unknownReason),
  },
  numberCell(METRICS.QL, true),
  numberCell(METRICS.TRIAL_SCHEDULED, true),
  numberCell(METRICS.TRIAL_DONE, true),
  convCell('ql_td', 'QL → TD', METRICS.TRIAL_DONE, METRICS.QL, true),
  numberCell(METRICS.ENROLLED, true),
  convCell('td_enr', 'TD → Enr', METRICS.ENROLLED, METRICS.TRIAL_DONE, true),
  convCell('ql_enr', 'QL → Enr', METRICS.ENROLLED, METRICS.QL, true),
];

// Pair table: signup page + the landing page that put the lead in this
// category (rows are signup x landing pairs; both columns sortable).
export const buildPageLandingColumns = () => [
  {
    Header: 'Page (signed up on)',
    accessor: 'label',
    sortable: true,
    minWidth: 200,
    Cell: row => labelText(row.value, UNKNOWN_REASONS.SIGNUP_PAGE),
  },
  {
    Header: 'Landing page (why this category)',
    accessor: 'landing',
    sortable: true,
    minWidth: 200,
    Cell: row => labelText(row.value, UNKNOWN_REASONS.LANDING_PAGE, 'body3', UI.body),
  },
  ...METRIC_AND_CONV_COLUMNS().map(column => ({ ...column, sortable: true })),
];

const footerText = value => (
  <Text variant="body2" color={UI.ink}>
    {value}
  </Text>
);

// Attach a totals footer: metric columns sum, conversion columns show the
// overall percentage, the label column reads 'Total'.
const withTotalsFooter = (columns, rows) => {
  const totals = sumRows(rows);
  const convFooter = (numerator, denominator) => {
    const pct = conversionPct(totals[numerator], totals[denominator]);
    return footerText(pct === null ? '—' : `${pct}%`);
  };
  return columns.map(column => {
    if (column.accessor === 'label' || column.accessor === 'channel') {
      return { ...column, Footer: footerText('Total') };
    }
    if (typeof column.accessor === 'string' && totals[column.accessor] !== undefined) {
      return { ...column, Footer: footerText(totals[column.accessor].toLocaleString()) };
    }
    if (column.id === 'ql_td') return { ...column, Footer: convFooter(METRICS.TRIAL_DONE, METRICS.QL) };
    if (column.id === 'td_enr') return { ...column, Footer: convFooter(METRICS.ENROLLED, METRICS.TRIAL_DONE) };
    if (column.id === 'ql_enr') return { ...column, Footer: convFooter(METRICS.ENROLLED, METRICS.QL) };
    if (column.id === 'share') return { ...column, Footer: footerText('100%') };
    return column;
  });
};

// Shared react-table wrapper. No pagination — callers pass a bounded row set
// (totals + explicitly requested rows), so every row is always visible.
// `showTotals` appends a totals footer row (sums + overall conversion %).
const FunnelTable = ({
  data,
  columns,
  loading,
  defaultSorted,
  emptyMessage = EMPTY_MESSAGE,
  showTotals,
}) => {
  const rows = Array.isArray(data) ? data : [];
  const tableColumns = showTotals && rows.length ? withTotalsFooter(columns, rows) : columns;
  return (
    <TableCard>
      <ReactTable
        data={rows}
        columns={tableColumns}
        loading={!!loading}
        defaultSorted={defaultSorted || []}
        showPagination={false}
        pageSize={Math.max(rows.length, 1)}
        minRows={rows.length ? 0 : 3}
        sortable
        resizable={false}
        NoDataComponent={() =>
          loading ? null : (
            <EmptyCell>
              <Text variant="body2" color={UI.muted}>{emptyMessage}</Text>
            </EmptyCell>
          )
        }
        className="-highlight"
      />
    </TableCard>
  );
};

export default FunnelTable;
